import { ErrorMessage } from "@minervaproject/fds";
import React, { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";

import { redeemInvitation, validateInvitation } from "../api";
import { useIsMobile } from "../hooks";
import { InvitationStore } from "../store/invitation";
import { LoginStore } from "../store/login";
import { passwordValidator } from "src/utils";

import { Logo } from "./logo";

const SetPassword = () => {
  const formRef = useRef(null);
  const history = useHistory();
  const dispatch = useDispatch();
  const isMobile = useIsMobile();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const invitation = useSelector(InvitationStore.selectors.select());
  const loginDetails = useSelector(LoginStore.selectors.select());
  const { cookieBannerIsShowing } = loginDetails;
  const { hasRedeemedInvitation, hasValidInvitation } = invitation;
  const { invitationId, orgId } = useParams();
  const { register, errors, handleSubmit, watch } = useForm();
  if (!invitationId || !orgId) history.push("/welcome");

  useEffect(focusOnInput, [hasValidInvitation]);
  useEffect(checkInvitationIsValid, [dispatch, hasValidInvitation, invitationId, orgId]);
  useEffect(handleInvitationRedemption, [hasRedeemedInvitation]);

  // NOTE: default value is null, which means we haven't validated or invalidated the token
  // if it is false, we assume it's invalid and send the user to the login page
  if (hasValidInvitation === null) return null;
  if (!hasValidInvitation) {
    const baseUrl = `${location.protocol}//${location.host}`;
    location.href = `${baseUrl}`;
  }

  return (
    <div
      style={{ height: `calc(100vh - ${cookieBannerIsShowing ? "72px" : "0px"})` }}
      className="flex column"
    >
      <Logo className="absolute m10" />
      <div className={`self-center ${isMobile ? "mx5 full-width" : "mx-auto"}`}>
        <div className="flex flex-column items-center">
          {showConfirmation ? (
            <div
              id="password-success"
              className="mb6"
              style={{ width: isMobile ? "100%" : "400px" }}
            >
              <h2 className="text-black-tint-20 h2 mb10">Password Created</h2>
              <p className="body pr2 m0">
                Thank you. Your new password has been successfully created.
              </p>
              <button
                onClick={() => history.push("/welcome")}
                className="block button-primary mt5"
                type="submit"
              >
                Next
              </button>
            </div>
          ) : (
            <div>
              <div className="mb6" style={{ width: isMobile ? "100%" : "400px" }}>
                <h2 className="text-black-tint-20 h2 mb10">Welcome to Forum</h2>
                <p className="body pr2 m0">
                  To secure your account, we’ll need you to create a unique password. It must be at
                  least eight characters long and contain one upper case letter and one number.
                </p>
              </div>
              <form ref={formRef} onSubmit={handleSubmit(onSubmit)}>
                <label className="mb5">
                  <p className="text-black-tint-40 bold body-s mb1">New Password</p>
                  <input
                    name="password"
                    id="js-new-password"
                    className="fds-input body-s"
                    type="password"
                    style={{
                      outlineColor: errors.password ? "var(--red)" : "var(--blue)",
                    }}
                    ref={register({
                      required: "Please provide a valid password.",
                      validate: passwordValidator({
                        email: loginDetails.email || window.context?.email,
                      }),
                    })}
                  />
                  {errors.password && <ErrorMessage msg={errors.password.message} />}
                </label>

                <label className="mb5">
                  <p className="text-black-tint-40 bold body-s mb1">Confirm Password</p>
                  <input
                    name="password_repeat"
                    id="js-password-repeat"
                    className="fds-input body-s"
                    type="password"
                    style={{
                      outlineColor: errors.password_repeat ? "var(--red)" : "var(--blue)",
                    }}
                    ref={register({
                      required: "Please confirm your new password.",
                      validate: (value) =>
                        value === watch("password") ? true : "The passwords do not match.",
                    })}
                  />
                  {errors.password_repeat && <ErrorMessage msg={errors.password_repeat.message} />}
                </label>

                <button
                  id="reset"
                  type="submit"
                  style={{ width: "100%" }}
                  className="block button-primary mt5"
                >
                  Create Password
                </button>
                {errors.server_error && (
                  <ErrorMessage msgStyles={{ width: "400px" }} msg={errors.server_error.message} />
                )}
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  function focusOnInput() {
    if (hasValidInvitation) formRef.current.querySelector("input").focus();
  }

  async function onSubmit({ password }, e) {
    e.preventDefault();
    dispatch(redeemInvitation(invitationId, orgId, password));
  }

  function handleInvitationRedemption() {
    if (hasRedeemedInvitation) setShowConfirmation(true);
  }

  function checkInvitationIsValid() {
    if (hasValidInvitation === null) dispatch(validateInvitation(invitationId, orgId));
  }
};

export { SetPassword };
